import { FC } from "react";

import { Box, Column, Row } from "@hightouch/htui";
import { GraphTooltipProps } from "./types";
import {
  GraphTooltipWrapper,
  GraphTooltipTitle,
  GraphTooltipSubtitle,
  GraphTooltipValue,
  GraphTooltipValueDescription,
  GraphTooltipColor,
} from "./components";

/**
 * Tooltip to use for graphs, when Hightouch UI Tooltip is not sufficient.
 * Can be used either with recharts, or as children within Hightouch UI Tooltip.
 */
export const GraphTooltip: FC<GraphTooltipProps> = ({
  wrapperVariant,
  wrapperProps = {},
  color,
  title,
  subtitles = [],
  value,
  valueDescription,
}) => {
  const tooltipContent = (
    <Row gap={2}>
      <GraphTooltipColor color={color} />
      <Column align="flex-start" gap={1} sx={{ span: { textAlign: "left" } }}>
        {title && <GraphTooltipTitle>{title}</GraphTooltipTitle>}
        {subtitles.map((subtitle, index) => (
          <GraphTooltipSubtitle key={`subtitle-${index}-${subtitle}`}>
            {subtitle}
          </GraphTooltipSubtitle>
        ))}
        {value?.map((val, index) => {
          // Generate a stable key based on the value content
          const key =
            typeof val === "string" || typeof val === "number"
              ? `value-${index}-${val}`
              : `value-${index}-${val.value}-${val.description ?? ""}`;
          return <GraphTooltipValue key={key} value={val} />;
        })}
        {valueDescription && (
          <GraphTooltipValueDescription>
            {valueDescription}
          </GraphTooltipValueDescription>
        )}
      </Column>
    </Row>
  );

  if (wrapperVariant === "full") {
    return (
      <GraphTooltipWrapper {...wrapperProps}>
        {tooltipContent}
      </GraphTooltipWrapper>
    );
  }

  if (wrapperVariant === "minimal") {
    return (
      // Need to increase spacing from standard HTUI tooltip to match GraphTooltipWrapper
      <Box py={1} px={0.5} pos="relative" {...wrapperProps}>
        {tooltipContent}
      </Box>
    );
  }

  return tooltipContent;
};
